// src/pages/KarigarManagement/Dashboard/KarigarDashboard.jsx

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useKarigarManagement,
  useJobManagement,
  usePaymentManagement,
} from "@/hooks/useKarigar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Button } from "@/components/ui-v2/button";
import { Badge } from "@/components/ui-v2/badge";
import {
  Users,
  Briefcase,
  CreditCard,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  IndianRupee,
  Clock,
  Package,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { format } from "date-fns";

// ============================================
// HELPER: Get status badge variant
// ============================================
const getStatusBadgeVariant = (status) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case "completed":
    case "delivered":
      return "success";
    case "in_progress":
    case "assigned":
      return "default";
    case "pending":
    case "quality_check":
      return "warning";
    case "cancelled":
    case "rejected":
      return "destructive";
    default:
      return "secondary";
  }
};

// ============================================
// HELPER: Format status for display
// ============================================
const formatStatus = (status) => {
  if (!status) return "Unknown";
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const KarigarDashboard = () => {
  const navigate = useNavigate();

  const {
    stats: karigarStats,
    loading: karigarLoading,
    getStats: getKarigarStats,
  } = useKarigarManagement();

  const {
    stats: jobStats,
    loading: jobLoading,
    getStats: getJobStats,
  } = useJobManagement();

  const {
    stats: paymentStats,
    loading: paymentLoading,
    getStats: getPaymentStats,
  } = usePaymentManagement();

  // Fetch stats on mount
  useEffect(() => {
    getKarigarStats();
    getJobStats();
    getPaymentStats();
  }, []);

  // ============================================
  // EXTRACT DATA FROM STATS (with safe defaults)
  // ============================================

  // Karigar data
  const activeKarigarsCount = karigarStats?.overview?.active || 0;
  const recentKarigars = Array.isArray(karigarStats?.recentKarigars)
    ? karigarStats.recentKarigars
    : [];

  // Job data - FIXED to match API response structure
  // API returns: { byStatus: { DELIVERED: 3, CANCELLED: 1 }, byPriority: {...}, overdueCount: 0, ... }
  const jobByStatus = jobStats?.byStatus || {};
  const jobByPriority = jobStats?.byPriority || {};
  const overdueCount = jobStats?.overdueCount || 0;
  const totalJobs = jobStats?.totalJobs || 0;
  const avgCompletionTimeHours = jobStats?.avgCompletionTimeHours || 0;

  // Handle recentJobs - may come as array or may not exist
  const recentJobs = Array.isArray(jobStats?.recentJobs)
    ? jobStats.recentJobs
    : [];

  // Calculate active jobs from byStatus object
  // Active statuses: assigned, in_progress, quality_check (case-insensitive)
  const activeStatuses = [
    "assigned",
    "in_progress",
    "quality_check",
    "ASSIGNED",
    "IN_PROGRESS",
    "QUALITY_CHECK",
  ];
  const activeJobsCount = Object.entries(jobByStatus).reduce(
    (sum, [status, count]) => {
      if (activeStatuses.includes(status)) {
        return sum + count;
      }
      return sum;
    },
    0
  );

  // Calculate completed jobs
  const completedStatuses = [
    "completed",
    "delivered",
    "COMPLETED",
    "DELIVERED",
  ];
  const completedJobsCount = Object.entries(jobByStatus).reduce(
    (sum, [status, count]) => {
      if (completedStatuses.includes(status)) {
        return sum + count;
      }
      return sum;
    },
    0
  );

  // Payment data - FIXED to match API response structure
  // API returns: { statusSummary: [...], monthlySummary: [...], pendingByKarigar: [...], recentPayments: [...] }
  const statusSummary = Array.isArray(paymentStats?.statusSummary)
    ? paymentStats.statusSummary
    : [];
  const pendingByKarigar = Array.isArray(paymentStats?.pendingByKarigar)
    ? paymentStats.pendingByKarigar
    : [];
  const recentPayments = Array.isArray(paymentStats?.recentPayments)
    ? paymentStats.recentPayments
    : [];

  // Calculate pending payments totals from pendingByKarigar
  const pendingPaymentsCount = pendingByKarigar.reduce(
    (sum, k) => sum + (k.count || 0),
    0
  );
  const pendingPaymentsTotal = pendingByKarigar.reduce(
    (sum, k) => sum + (k.totalAmount || k.total || 0),
    0
  );

  // Calculate total completed payments from statusSummary
  const completedPaymentsSummary = statusSummary.find(
    (s) => s._id === "completed"
  );
  const totalCompletedPayments = completedPaymentsSummary?.total || 0;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const isLoading =
    karigarLoading?.stats || jobLoading?.stats || paymentLoading?.stats;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Karigar Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of karigars, jobs, and payments
        </p>
      </div>

      {/* Stats Cards - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Active Karigars */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/karigar-management/karigars")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Karigars</p>
                <p className="text-3xl font-bold">{activeKarigarsCount}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Jobs */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() =>
            navigate("/karigar-management/jobs?status=in_progress")
          }
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Jobs</p>
                <p className="text-3xl font-bold">{activeJobsCount}</p>
                <p className="text-xs text-muted-foreground">
                  of {totalJobs} total
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Payments */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() =>
            navigate("/karigar-management/payments?status=pending")
          }
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Pending Payments
                </p>
                <p className="text-3xl font-bold">{pendingPaymentsCount}</p>
                {pendingPaymentsTotal > 0 && (
                  <p className="text-sm text-orange-600">
                    {formatCurrency(pendingPaymentsTotal)}
                  </p>
                )}
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overdue Jobs */}
        <Card
          className={`cursor-pointer hover:shadow-md transition-shadow ${
            overdueCount > 0 ? "border-red-200 bg-red-50" : ""
          }`}
          onClick={() => navigate("/karigar-management/jobs?overdue=true")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue Jobs</p>
                <p
                  className={`text-3xl font-bold ${
                    overdueCount > 0 ? "text-red-600" : ""
                  }`}
                >
                  {overdueCount}
                </p>
              </div>
              <div
                className={`h-12 w-12 rounded-full flex items-center justify-center ${
                  overdueCount > 0 ? "bg-red-100" : "bg-green-100"
                }`}
              >
                {overdueCount > 0 ? (
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                ) : (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards - Row 2 (Additional Stats) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Completed Jobs */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/karigar-management/jobs?status=delivered")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed Jobs</p>
                <p className="text-3xl font-bold text-green-600">
                  {completedJobsCount}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Avg Completion Time */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Completion</p>
                <p className="text-3xl font-bold">
                  {avgCompletionTimeHours > 0
                    ? `${Math.round(avgCompletionTimeHours)}h`
                    : "-"}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Payments Completed */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() =>
            navigate("/karigar-management/payments?status=completed")
          }
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Payments Done</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(totalCompletedPayments)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <IndianRupee className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job Status Breakdown */}
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-2">Job Status</p>
            <div className="space-y-1">
              {Object.entries(jobByStatus).length > 0 ? (
                Object.entries(jobByStatus)
                  .slice(0, 3)
                  .map(([status, count]) => (
                    <div
                      key={status}
                      className="flex justify-between items-center text-sm"
                    >
                      <span className="text-muted-foreground">
                        {formatStatus(status)}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {count}
                      </Badge>
                    </div>
                  ))
              ) : (
                <p className="text-sm text-muted-foreground">No jobs yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Jobs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle>Recent Jobs</CardTitle>
              <CardDescription>Latest job assignments</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/karigar-management/jobs")}
            >
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentJobs.length > 0 ? (
                recentJobs.map((job) => (
                  <div
                    key={job._id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted"
                    onClick={() =>
                      navigate(`/karigar-management/jobs/${job._id}`)
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {job.jobNumber ||
                            job.jobCode ||
                            `Job #${job._id.slice(-6)}`}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {job.karigarName || job.karigar?.name || "Unassigned"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={getStatusBadgeVariant(job.status)}
                        className="mb-1"
                      >
                        {formatStatus(job.status)}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {job.expectedCompletionDate
                          ? `Due: ${format(
                              new Date(job.expectedCompletionDate),
                              "dd MMM"
                            )}`
                          : job.createdAt
                          ? format(new Date(job.createdAt), "dd MMM")
                          : ""}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Briefcase className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">No recent jobs</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => navigate("/karigar-management/jobs/create")}
                  >
                    Create your first job
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle>Recent Payments</CardTitle>
              <CardDescription>Latest payment activity</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/karigar-management/payments")}
            >
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentPayments.length > 0 ? (
                recentPayments.map((payment) => (
                  <div
                    key={payment._id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted"
                    onClick={() =>
                      navigate(`/karigar-management/payments/${payment._id}`)
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <IndianRupee className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {payment.karigarName ||
                            payment.karigar?.name ||
                            "Unknown"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {/* FIXED: Use paymentReference instead of paymentNumber */}
                          {payment.paymentReference ||
                            payment.paymentNumber ||
                            `#${payment._id.slice(-6)}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">
                        {/* FIXED: Use netAmount instead of amount */}
                        {formatCurrency(
                          payment.netAmount ?? payment.amount ?? 0
                        )}
                      </p>
                      <Badge
                        variant={getStatusBadgeVariant(payment.status)}
                        className="text-xs"
                      >
                        {formatStatus(payment.status)}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">No recent payments</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Karigars */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle>Recent Karigars</CardTitle>
              <CardDescription>Recently added karigars</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/karigar-management/karigars")}
            >
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentKarigars.length > 0 ? (
                recentKarigars.map((karigar, index) => (
                  <div
                    key={karigar._id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted"
                    onClick={() =>
                      navigate(`/karigar-management/karigars/${karigar._id}`)
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold">
                        #{index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{karigar.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {karigar.karigarCode}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={
                          karigar.status === "active" ? "default" : "secondary"
                        }
                      >
                        {formatStatus(karigar.status)}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {karigar.createdAt
                          ? format(new Date(karigar.createdAt), "dd MMM yyyy")
                          : ""}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">No karigars yet</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() =>
                      navigate("/karigar-management/karigars/create")
                    }
                  >
                    Add your first karigar
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-20 flex-col"
                onClick={() => navigate("/karigar-management/karigars/create")}
              >
                <Users className="h-6 w-6 mb-2" />
                Add Karigar
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col"
                onClick={() => navigate("/karigar-management/jobs/create")}
              >
                <Briefcase className="h-6 w-6 mb-2" />
                Create Job
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col"
                onClick={() =>
                  navigate("/karigar-management/payments?status=pending")
                }
              >
                <CreditCard className="h-6 w-6 mb-2" />
                Process Payments
              </Button>
              {/* <Button
                variant="outline"
                className="h-20 flex-col"
                onClick={() => navigate("/karigar-management/reports")}
              >
                <TrendingUp className="h-6 w-6 mb-2" />
                View Reports
              </Button> */}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KarigarDashboard;
